import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getToken, API } from "@/lib/api";
import { Trash2, Download } from "lucide-react";

export default function AdminContacts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/contacts");
      setItems(r.data);
      if (r.data.length && !active) setActive(r.data[0]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (c) => {
    if (!window.confirm("Delete this submission?")) return;
    await api.delete(`/admin/contacts/${c.id}`);
    setActive(null);
    load();
    toast.success("Deleted.");
  };

  const downloadCsv = async () => {
    const token = getToken();
    const res = await fetch(`${API}/admin/contacts.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "contacts.csv";
    a.click();
  };

  return (
    <div data-testid="admin-contacts">
      <div className="flex items-baseline justify-between mb-10">
        <div>
          <div className="eyebrow mb-2">Inbox</div>
          <h1 className="display text-[36px]">Contact submissions</h1>
        </div>
        <button
          onClick={downloadCsv}
          data-testid="export-contacts"
          className="inline-flex items-center gap-2 rounded-full border border-brand-ink/25 px-5 py-2.5 text-[13.5px] text-brand-ink hover:border-brand-blue hover:text-brand-blue transition-colors"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {loading ? (
        <p className="text-brand-ink/50 text-[14px]">Loading…</p>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-brand-rule bg-brand-cream/40 p-10 text-center">
          <p className="text-brand-ink/70">Nothing yet. Messages will appear here.</p>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
          <ul className="rounded-lg border border-brand-rule/70 bg-brand-cream overflow-hidden max-h-[70vh] overflow-y-auto">
            {items.map((c) => (
              <li key={c.id}>
                <button
                  onClick={() => setActive(c)}
                  data-testid={`contact-row-${c.id}`}
                  className={`w-full text-left px-5 py-4 border-b border-brand-rule/40 last:border-0 hover:bg-brand-paper transition-colors ${
                    active?.id === c.id ? "bg-brand-paper" : ""
                  }`}
                >
                  <div className="font-medium text-brand-ink text-[15px]">{c.name}</div>
                  <div className="text-[12.5px] text-brand-ink/60 mt-0.5">{c.email}</div>
                  <div className="text-[13px] text-brand-ink/70 mt-2 line-clamp-2">{c.message}</div>
                  <div className="text-[11px] text-brand-ink/40 mt-2">{new Date(c.created_at).toLocaleString()}</div>
                </button>
              </li>
            ))}
          </ul>

          {active && (
            <div className="rounded-lg border border-brand-rule/70 bg-brand-cream p-8" data-testid="contact-detail">
              <div className="flex items-baseline justify-between gap-4">
                <div>
                  <h2 className="font-serif text-[26px] text-brand-ink">{active.name}</h2>
                  <a href={`mailto:${active.email}`} className="text-brand-blue text-[14px] hover:underline">{active.email}</a>
                </div>
                <button
                  onClick={() => remove(active)}
                  data-testid="delete-contact"
                  className="inline-flex items-center gap-1.5 text-brand-ink/70 hover:text-red-600 text-[13px]"
                >
                  <Trash2 size={13} /> Delete
                </button>
              </div>
              <div className="text-[12px] text-brand-ink/50 mt-2">
                Received {new Date(active.created_at).toLocaleString()}
              </div>
              <div className="mt-6 whitespace-pre-wrap text-[15px] leading-relaxed text-brand-ink/85">
                {active.message}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
